import { useNavigate } from "react-router-dom";
import api from "@api";
import { getDirectoryLeagueUrl } from "../../../../utils/directoryLeagueUrl.js";
import "./LeagueListItem.css";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import updatedIcon from "../../Public/assets/images/updated.png";
import viewsIcon from "../../Public/assets/images/views.png";
import audienceIcon from "../../Public/assets/images/gender.png";
import programTypeIcon from "../../Public/assets/images/programs.png";
import ageIcon from "../../Public/assets/images/age.png";
import { getProgramTypeLabel } from "../../../../utils/programTypes";

// import heartIcon from "./images/heart-outline.png";

function LeagueListItem({ directoryLeague }) {
  const navigate = useNavigate();
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [profileLoading, setProfileLoading] = useState(false);
  const {
    name,
    city,
    state,
    description,
    is_verified,
    directory_logo,
    view_count = 0,
  } = directoryLeague;

  const programs = directoryLeague.programs || [];
  const detailUrl = getDirectoryLeagueUrl(directoryLeague);

  const formatLabel = (value = "") =>
    String(value)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const normalizeGenderArray = (gender) => {
    if (Array.isArray(gender)) return gender;

    if (typeof gender === "string") {
      const trimmed = gender.trim();

      if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        return trimmed
          .slice(1, -1)
          .split(",")
          .map((g) => g.trim().replace(/^"|"$/g, ""))
          .filter(Boolean);
      }

      return trimmed ? [trimmed] : [];
    }

    return [];
  };

  const validMinAges = programs
    .map((p) => Number(p.min_age))
    .filter((age) => Number.isFinite(age) && age > 0);

  const validMaxAges = programs
    .map((p) => Number(p.max_age))
    .filter((age) => Number.isFinite(age) && age > 0);

  const lowestMinAge = validMinAges.length ? Math.min(...validMinAges) : null;
  const highestMaxAge = validMaxAges.length ? Math.max(...validMaxAges) : null;

  const hasYouthProgram = programs.some((p) => {
    const min = Number(p.min_age);
    const max = Number(p.max_age);

    return (
      (Number.isFinite(min) && min < 18) ||
      (Number.isFinite(max) && max > 0 && max < 18)
    );
  });

  const hasAdultProgram = programs.some((p) => {
    const min = Number(p.min_age);
    const max = Number(p.max_age);

    const openEnded =
      p.max_age === null ||
      p.max_age === undefined ||
      p.max_age === "" ||
      max === 0;

    return (
      (Number.isFinite(min) && min >= 18) ||
      (Number.isFinite(min) && min < 18 && openEnded) ||
      (Number.isFinite(max) && max >= 18)
    );
  });

  let ageMode = "mixed";

  if (hasYouthProgram && !hasAdultProgram) {
    ageMode = "youth";
  } else if (hasAdultProgram && !hasYouthProgram) {
    ageMode = "adult";
  }

  const formatGenderLabel = (gender, mode) => {
    if (gender === "male") {
      if (mode === "youth") return "Boys";
      if (mode === "adult") return "Men";
      return "Male";
    }

    if (gender === "female") {
      if (mode === "youth") return "Girls";
      if (mode === "adult") return "Women";
      return "Female";
    }

    if (gender === "coed") return "Coed";

    return formatLabel(gender);
  };

  const programTypes = [
    ...new Set(programs.map((p) => p.program_type).filter(Boolean)),
  ];

  const programSports = [
    ...new Set(programs.map((p) => p.sport).filter(Boolean)),
  ];

  const programGenders = [
    ...new Set(
      programs.flatMap((p) =>
        normalizeGenderArray(p.gender).map((gender) =>
          formatGenderLabel(gender, ageMode)
        )
      )
    ),
  ];

  const hasOpenEndedProgram = programs.some((p) => {
    const min = Number(p.min_age);
    const max = Number(p.max_age);

    const maxAgeMissing =
      p.max_age === null ||
      p.max_age === undefined ||
      p.max_age === "" ||
      max === 0;

    return Number.isFinite(min) && min > 0 && maxAgeMissing;
  });

  const programAgeLabel =
    lowestMinAge && hasOpenEndedProgram
      ? `${lowestMinAge}+`
      : lowestMinAge && highestMaxAge
        ? `${lowestMinAge}-${highestMaxAge}`
        : lowestMinAge
          ? `${lowestMinAge}+`
          : highestMaxAge
            ? `${highestMaxAge} & under`
            : null;

  const displayDate =
    directoryLeague.listing_updated_at || directoryLeague.createdAt;

  const lastUpdatedLabel = displayDate
    ? formatDistanceToNow(new Date(displayDate), { addSuffix: true })
    : null;

  const formattedViews =
    view_count >= 1000000
      ? `${(view_count / 1000000).toFixed(1)}M`
      : view_count >= 1000
        ? `${(view_count / 1000).toFixed(1)}K`
        : String(view_count);

  const truncateWords = (text, maxWords) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return `${words.slice(0, maxWords).join(" ")}...`;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const shortDescription = truncateWords(description, isMobile ? 10 : 18);
  const locationLabel =
    city && state ? `${city}, ${state}` : city || state || "";

  const handleOpenProfile = async () => {
    if (profileLoading) return;

    setProfileLoading(true);

    try {
      const res = await api.get(`/api/directory/leagues/${directoryLeague.id}`);

      navigate(detailUrl, {
        state: {
          preloadedLeagueProfile: res.data,
        },
      });
    } catch (err) {
      console.error("Failed to preload league profile", err);
      navigate(detailUrl);
    } finally {
      setProfileLoading(false);
    }
  };






  return (
    <>
      <div
        className={`league-list-item ${profileLoading ? "league-list-item-loading" : ""}`}
        role="link"
        tabIndex={0}
        onClick={handleOpenProfile}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenProfile();
          }
        }}
      >
        <div className="league-list-layout">
          <div className="league-logo-column">
            <button
              type="button"
              className="league-logo-button"
              onClick={(e) => {
                e.stopPropagation();
                if (directory_logo) setShowLogoModal(true);
              }}
              disabled={!directory_logo}
              aria-label={directory_logo ? `View ${name} logo` : `${name} logo`}
            >
              <div className="league-logo-box">
                {directory_logo ? (
                  <img
                    src={directory_logo}
                    alt={`${name} logo`}
                    className="league-logo-image"
                  />
                ) : (
                  <span className="league-logo-placeholder">
                    {name?.charAt(0)?.toUpperCase() || "•"}
                  </span>
                )}
              </div>
            </button>
          </div>

          <div className="league-content-column">
            {locationLabel && (
              <div className="league-location-top">{locationLabel}</div>
            )}

            <div className="league-title-row">
              {is_verified && <span className="verified-star">⭐</span>}

              <span className="league-title-text">
                <span className="league-name">{name}</span>
              </span>

              {shortDescription && (
                <span className="league-description-inline">
                  {" "}
                  – {shortDescription}
                </span>
              )}
            </div>

            <div className="league-list-meta-row">
              {lastUpdatedLabel && (
                <span className="meta-item">
                  <img src={updatedIcon} alt="" className="meta-icon" />
                  <span>Updated {lastUpdatedLabel}</span>
                </span>
              )}

              {view_count >= 10 && (
                <span className="meta-item">
                  <img src={viewsIcon} alt="" className="meta-icon" />
                  <span>{formattedViews} views</span>
                </span>
              )}

              {programSports.length > 0 && (
                <span className="meta-item meta-item-sport">
                  <span>{programSports.map(formatLabel).join(", ")}</span>
                </span>
              )}

              {programTypes.length > 0 && (
                <span className="meta-item">
                  <img src={programTypeIcon} alt="" className="meta-icon" />
                  <span>{programTypes.map(getProgramTypeLabel).join(", ")}</span>
                </span>
              )}

              {programGenders.length > 0 && (
                <span className="meta-item">
                  <img src={audienceIcon} alt="" className="meta-icon" />
                  <span>{programGenders.join(", ")}</span>
                </span>
              )}

              {programAgeLabel && (
                <span className="meta-item">
                  <img src={ageIcon} alt="" className="meta-icon" />
                  <span>Ages {programAgeLabel}</span>
                </span>
              )}
            </div>
          </div>

          <div className="league-actions-column">
            {/* <button
              type="button"
              className="league-save-button"
              aria-label="Save listing"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={heartIcon} alt="" className="league-action-icon" />
            </button> */}

            <button
              type="button"
              className="league-chevron-link"
              aria-label={`View ${name}`}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenProfile();
              }}
            >
              {profileLoading ? "…" : "›"}
            </button>
          </div>
        </div>
      </div>

      {showLogoModal && directory_logo && (
        <div
          className="league-logo-modal-overlay"
          onClick={() => setShowLogoModal(false)}
        >
          <div
            className="league-logo-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="league-logo-modal-close"
              onClick={() => setShowLogoModal(false)}
              aria-label="Close logo preview"
            >
              ×
            </button>

            <img
              src={directory_logo}
              alt={`${name} logo`}
              className="league-logo-modal-image"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default LeagueListItem;